import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowUpRight, Bell, BookOpen, Brain, Check, ChevronDown, Clock3, Fingerprint, Layers3, MessageSquare, Package, Plug, Sparkles, X, UsersRound, Zap } from "lucide-react";
import { GlossTile, HERO_BACKGROUND } from "@/components/panels";
import releases from "./releases.json";
import { acknowledge, readSeen, seenKey } from "./seen";
import "./updates.css";

const icons = { skills: Layers3, automations: Clock3, updates: Sparkles, brain: Brain, chat: MessageSquare, alloy: Zap, connections: Plug, guides: BookOpen, team: UsersRound };
const kinds = { new: "New", improved: "Improved", fixed: "Fixed" };
const dateLabel = (date) => new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
const latest = releases[0];

function ReleaseItems({ items, onFollow, featured = false }) {
  return <ul className={`hu-items ${featured ? "hu-highlights" : ""}`}>
    {items.map((item, index) => <li key={item.title} className={`hu-item hu-${item.icon} ${featured && index === 0 ? "hu-featured" : ""}`}>
      <div className="hu-item-heading">
        <GlossTile color={item.icon === "automations" ? "#268477" : "#3269af"} icon={icons[item.icon]} size={42} />
        <span className="hu-area">{item.area}</span><span className={`hu-kind hu-kind-${item.kind}`}>{kinds[item.kind]}</span>
      </div>
      <h3>{item.title}</h3><p>{item.body}</p>
      {item.skillGroups && <div className="hu-origins" aria-label="Three skill origins"><span><Package size={14} />Default Hermes</span><span><Layers3 size={14} />GrowthOS</span><span><Fingerprint size={14} />Brand Specific</span></div>}
      {item.path && <Link to={item.path} onClick={(event) => { if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) onFollow(); }}>{item.action}<ArrowUpRight size={15} aria-hidden="true" /></Link>}
    </li>)}
  </ul>;
}

// The keyed inner component isolates identities without resetting on workspace changes.
export default function HubUpdates({ viewer, autoOpen = true }) {
  return viewer ? <UpdateDialog key={viewer} viewer={viewer} autoOpen={autoOpen} /> : null;
}

function UpdateDialog({ viewer, autoOpen }) {
  const [seen, setSeen] = useState(() => readSeen(viewer));
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const automatic = useRef(false);
  const history = useRef(null);
  const trigger = useRef(null);
  const unseen = latest.sequence > seen;

  useEffect(() => {
    if (!autoOpen || !unseen || open) return;
    let ready = false;
    let focusTimer;
    const tryOpen = () => {
      if (!ready) return;
      const current = readSeen(viewer);
      if (current >= latest.sequence) { setSeen(current); return; }
      if (document.visibilityState !== "visible" || document.querySelector('dialog[open], [role="dialog"], [role="alertdialog"], [role="menu"]')) return;
      if (document.activeElement?.matches('input, textarea, select, [contenteditable="true"]')) return;
      automatic.current = true;
      setOpen(true);
    };
    const timer = window.setTimeout(() => { ready = true; tryOpen(); }, 650);
    const observer = new MutationObserver(tryOpen);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["open", "role"] });
    const afterFocus = () => { window.clearTimeout(focusTimer); focusTimer = window.setTimeout(tryOpen, 0); };
    document.addEventListener("visibilitychange", tryOpen);
    document.addEventListener("focusout", afterFocus);
    return () => { window.clearTimeout(timer); window.clearTimeout(focusTimer); observer.disconnect(); document.removeEventListener("visibilitychange", tryOpen); document.removeEventListener("focusout", afterFocus); };
  }, [autoOpen, unseen, open, viewer]);

  useEffect(() => {
    const sync = (event) => {
      if (event.key !== seenKey(viewer)) return;
      const next = readSeen(viewer);
      setSeen(next);
      if (automatic.current && next >= latest.sequence) { setOpen(false); setHistoryOpen(false); automatic.current = false; }
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [viewer]);

  const dismiss = () => {
    setSeen(acknowledge(viewer, latest.sequence));
    setOpen(false); setHistoryOpen(false); automatic.current = false;
  };

  return <Dialog.Root open={open} onOpenChange={(next) => { if (next) { automatic.current = false; setOpen(true); } else dismiss(); }}>
    <Dialog.Trigger asChild><button ref={trigger} type="button" className="hu-trigger" data-testid="hub-updates-trigger" aria-label={unseen ? "What’s new — unread updates" : "What’s new"}><Bell size={16} aria-hidden="true" /><span>What’s new</span>{unseen && <i aria-hidden="true" />}</button></Dialog.Trigger>
    <Dialog.Portal>
      <Dialog.Overlay className="hu-overlay" />
      <Dialog.Content className="hu-popup" data-testid="hub-updates" onCloseAutoFocus={(event) => { event.preventDefault(); trigger.current?.focus({ preventScroll: true }); }}>
        <header className="hu-hero" style={{ background: HERO_BACKGROUND }}>
          <div className="hu-hero-top"><span className="hu-product"><Sparkles size={15} aria-hidden="true" />HUB <span>Product updates</span></span><Dialog.Close className="hu-close" aria-label="Close updates"><X size={19} /></Dialog.Close></div>
          <div className="hu-release-date"><span className="hu-latest"><i />Latest release</span><time dateTime={latest.date}>{dateLabel(latest.date)}</time><span className="hu-version">Version {latest.version}</span></div>
          <Dialog.Title className="hu-title">What’s new in HUB</Dialog.Title>
          <Dialog.Description className="hu-intro">{latest.summary}</Dialog.Description>
        </header>
        <div className="hu-scroll">
          <div className="hu-section-label"><span>Latest updates</span><span>{latest.items.length} updates</span></div>
          <ReleaseItems items={latest.items} onFollow={dismiss} featured />
          <details ref={history} className="hu-history" open={historyOpen} onToggle={(event) => setHistoryOpen(event.currentTarget.open)}>
            <summary><span className="hu-history-icon"><Clock3 size={19} /></span><span>Previous updates<small>Browse earlier updates by date.</small></span><span className="hu-count">{releases.length - 1}</span><ChevronDown size={17} className="hu-chevron" /></summary>
            <div className="hu-timeline">{releases.slice(1).map((release) => <details className="hu-past" key={release.id}>
              <summary>
                <span className="hu-date-tile" aria-hidden="true"><span className="hu-date-month">{dateLabel(release.date).split(" ")[0]}</span><span className="hu-date-day">{Number(release.date.slice(8, 10))}</span></span>
                <span className="hu-past-info">
                  <span className="hu-past-meta"><time className="hu-past-date" dateTime={release.date}>{dateLabel(release.date)}</time><span className={`hu-release-badge ${release.version ? "hu-release-numbered" : "hu-release-early"}`}>{release.version ? `Version ${release.version}` : "Early release"}</span></span>
                  <strong>{release.title}</strong>
                  <span className="hu-past-count">{release.items.length} {release.items.length === 1 ? "update" : "updates"}</span>
                </span>
                <span className="hu-past-toggle" aria-hidden="true"><ChevronDown size={18} className="hu-chevron" /></span>
              </summary>
              <div className="hu-past-body"><p>{release.summary}</p><ReleaseItems items={release.items} onFollow={dismiss} /></div>
            </details>)}</div>
          </details>
        </div>
        <footer className="hu-footer"><button type="button" className="hu-history-button" onClick={() => { setHistoryOpen(true); window.requestAnimationFrame(() => history.current?.querySelector("summary")?.focus()); }}><Clock3 size={16} />All updates<span className="hu-count">{releases.length}</span></button><Dialog.Close className="hu-done"><Check size={16} />Got it</Dialog.Close></footer>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}
